import { networkMonitor } from "@/services/monitoring/network-monitor/network-monitor";
import { calculateBackoffMs } from "@/utils/backoff";
import { captureException } from "@/utils/sentry";
import type { Client } from "urql";

import { ClaimOnCourseMeasurementByPhoneMutation } from "../api/documents/claim-shot";
import { getEffectiveGraphqlBaseUrl } from "../api/graphql-url-override/graphql-url-override";
import { createApiClient } from "../api/graphql/client";
import { logClaimAttempt } from "./claim-debug-log";
import { claimQueueManager } from "./claim-queue-manager";
import type { ClaimQueueEntry } from "./types";

const SYNC_INTERVAL_MS = 60_000;
const BASE_BACKOFF_MS = 5_000;
const MAX_BACKOFF_MS = 300_000; // 5 minutes
const MAX_RETRY_COUNT = 12; // ~1 hour worst-case with 5-min cap; dead-letter entries are removed
const MUTATION_TIMEOUT_MS = 20_000;

export function calculateBackoff(retryCount: number): number {
  return calculateBackoffMs(retryCount, {
    baseMs: BASE_BACKOFF_MS,
    maxMs: MAX_BACKOFF_MS,
  });
}

export function isReadyForRetry(entry: ClaimQueueEntry): boolean {
  if (entry.retryCount >= MAX_RETRY_COUNT) {
    return false;
  }
  if (!entry.lastRetryAt) {
    return true;
  }
  const elapsed = Date.now() - entry.lastRetryAt;
  return elapsed >= calculateBackoff(entry.retryCount);
}

const TRANSIENT_STATUS_CODES = new Set([408, 429, 502, 503, 504]);

export function isTransientError(error: unknown): boolean {
  if (!navigator.onLine) {
    return true;
  }

  if (error && typeof error === "object") {
    // Check urql CombinedError networkError
    const networkError = (error as { networkError?: { statusCode?: number } })
      .networkError;
    if (networkError) {
      if (
        networkError.statusCode &&
        TRANSIENT_STATUS_CODES.has(networkError.statusCode)
      ) {
        return true;
      }
      return true; // Any network error is transient
    }
  }

  if (error instanceof Error) {
    const { name, message } = error;
    if (
      name === "AbortError" ||
      name === "TimeoutError" ||
      name === "NetworkError"
    ) {
      return true;
    }
    const lowerMsg = message.toLowerCase();
    if (
      lowerMsg.includes("network") ||
      lowerMsg.includes("fetch") ||
      lowerMsg.includes("timeout")
    ) {
      return true;
    }
  }

  return false;
}

class ClaimSyncEngine {
  private syncing = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private removeNetworkListener: (() => void) | null = null;
  private removeVisibilityListener: (() => void) | null = null;
  private removeFocusListener: (() => void) | null = null;
  private removePageshowListener: (() => void) | null = null;
  private client: Client | null = null;

  start(): void {
    void this.refreshClient();

    this.removeNetworkListener = networkMonitor.addListener((event) => {
      if (event.type === "online") {
        void this.sync();
      }
    });

    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        networkMonitor.isCurrentlyOnline()
      ) {
        void this.sync();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    this.removeVisibilityListener = () =>
      document.removeEventListener("visibilitychange", handleVisibility);

    const handleFocus = () => {
      if (networkMonitor.isCurrentlyOnline()) {
        void this.sync();
      }
    };
    window.addEventListener("focus", handleFocus);
    this.removeFocusListener = () =>
      window.removeEventListener("focus", handleFocus);

    const handlePageshow = (e: PageTransitionEvent) => {
      if (e.persisted && networkMonitor.isCurrentlyOnline()) {
        void this.sync();
      }
    };
    window.addEventListener("pageshow", handlePageshow);
    this.removePageshowListener = () =>
      window.removeEventListener("pageshow", handlePageshow);

    this.intervalId = setInterval(() => {
      if (networkMonitor.isCurrentlyOnline() && !this.syncing) {
        void this.sync();
      }
    }, SYNC_INTERVAL_MS);

    if (networkMonitor.isCurrentlyOnline()) {
      void this.sync();
    }
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.removeNetworkListener?.();
    this.removeNetworkListener = null;
    this.removeVisibilityListener?.();
    this.removeVisibilityListener = null;
    this.removeFocusListener?.();
    this.removeFocusListener = null;
    this.removePageshowListener?.();
    this.removePageshowListener = null;
  }

  async sync(): Promise<void> {
    if (this.syncing) {
      return;
    }
    this.syncing = true;

    try {
      await claimQueueManager.resetStuckSyncing();

      const entries = await claimQueueManager.getSyncable();

      for (const entry of entries) {
        if (entry.retryCount >= MAX_RETRY_COUNT) {
          captureException(
            new Error("Claim permanently abandoned after max retries"),
            {
              measurementId: entry.measurementId,
              retryCount: entry.retryCount,
              createdAt: new Date(entry.createdAt).toISOString(),
            }
          );
          await claimQueueManager.remove(entry.id);
        }
      }

      const retryable = entries
        .filter((e) => e.retryCount < MAX_RETRY_COUNT)
        .filter(isReadyForRetry);

      for (const entry of retryable) {
        if (!networkMonitor.isCurrentlyOnline()) {
          break;
        }
        await this.processEntry(entry);
      }
    } finally {
      this.syncing = false;
    }
  }

  private async createClient(): Promise<Client> {
    const url = await getEffectiveGraphqlBaseUrl();
    return createApiClient(url || "/graphql");
  }

  /**
   * Recreates the internal GraphQL client, picking up the current
   * GraphQL URL (including a developer override, if set).
   */
  async refreshClient(): Promise<void> {
    this.client = await this.createClient();
  }

  private async processEntry(entry: ClaimQueueEntry): Promise<void> {
    await claimQueueManager.markSyncing(entry.id);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
    const startedAt = performance.now();
    const logAttempt = (
      log: Omit<
        Parameters<typeof logClaimAttempt>[0],
        "measurementId" | "phoneNumber" | "introText" | "durationMs"
      >
    ) =>
      logClaimAttempt({
        measurementId: entry.measurementId,
        phoneNumber: entry.phoneNumber,
        introText: entry.introText,
        durationMs: performance.now() - startedAt,
        ...log,
      });

    try {
      const client = this.client ?? (await this.createClient());
      const result = await Promise.race([
        client
          .mutation(ClaimOnCourseMeasurementByPhoneMutation, {
            measurementId: entry.measurementId,
            phoneNumber: entry.phoneNumber,
            introText: entry.introText,
          })
          .toPromise(),
        new Promise<never>((_, reject) =>
          controller.signal.addEventListener("abort", () =>
            reject(new DOMException("Claim mutation timed out", "TimeoutError"))
          )
        ),
      ]);

      if (result.error) {
        if (isTransientError(result.error)) {
          await claimQueueManager.markFailed(entry.id);
          logAttempt({ outcome: "queued", error: result.error.message });
          return;
        }
        // Permanent GraphQL error: still remove from queue to avoid infinite retry
        console.error(
          `[ClaimSync] Permanent error for ${entry.measurementId}, removing:`,
          result.error.message
        );
        await claimQueueManager.remove(entry.id);
        logAttempt({ outcome: "rejected", error: result.error.message });
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isSuccess = (result.data?.claimOnCourseMeasurementByPhone as any)
        ?.appMutationResult?.isSuccess;

      if (isSuccess) {
        await claimQueueManager.remove(entry.id);
        console.log(`[ClaimSync] Synced claim for ${entry.measurementId}`);
        logAttempt({ outcome: "sent", status: 200 });
        return;
      }

      // Business logic failure (isSuccess === false): remove, not retryable
      console.error(
        `[ClaimSync] Business failure for ${entry.measurementId}, removing from queue`
      );
      await claimQueueManager.remove(entry.id);
      logAttempt({ outcome: "rejected", error: "isSuccess=false" });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (isTransientError(error)) {
        await claimQueueManager.markFailed(entry.id);
        logAttempt({ outcome: "queued", error: message });
        return;
      }
      // Unknown error: mark failed but keep in queue for manual review
      await claimQueueManager.markFailed(entry.id);
      console.error(
        `[ClaimSync] Unexpected error for ${entry.measurementId}:`,
        error
      );
      logAttempt({ outcome: "failed", error: message });
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

export const claimSyncEngine = new ClaimSyncEngine();
